import {
  FloatingActions,
  useFloatingActionControl,
} from '$tablet-app/src/app/components/FloatingActions';
import { Panel } from '$tablet-app/src/app/components/Panel';
import { ScreenHeader } from '$tablet-app/src/app/components/ScreenHeader';
import { ScreenTitle } from '$tablet-app/src/app/components/ScreenTitle';
import { openSMSApp } from '$tablet-app/src/app/phone';
import { openPhone } from '$tablet-app/src/app/phone/dialNumber/dialNumber';
import { color, palette, spacing } from '$tablet-app/src/app/theme';
import { useContactsInfoVideo } from '$tablet-app/src/app/videos/hooks/info_video_hooks';
import { Button } from '@akin-home/buttons/native';
import { ContactModel } from '@akin-home/contacts';
import { TextInput } from '@akin-home/forms/native';
import { Text } from '@akin-home/text/native';
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome';
import React, { FC, useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import Animated from 'react-native-reanimated';
import styled from 'styled-components/native';
import ContactRow from '../ContactRow';

interface Props {
  search_input?: string;
  favourites: any[];
  contacts: ContactModel[];
  filters: any[];
  isAllSelected: boolean;
  onSelectAll: any;
  onFilterPress: any;
  onSubmitSearch: any;

  onPressViewContact?: (contact: ContactModel) => void;
  onPressCallContact?: (contact: ContactModel) => void;
  onPressVideoCallContact?: (contact: ContactModel) => void;
  onPressMessageContact?: (contact: ContactModel) => void;
  onPressFavourite?: (contact: ContactModel) => void;
  onPressAdd?: () => void;
}

/** State Provider for Goals Screen */
const ContactsScreenView: FC<Props> = (props) => {
  const { scrollHandler, floating_action_styles } = useFloatingActionControl();

  const { data } = useContactsInfoVideo();

  const info_video_source = useMemo(() => {
    return data?.value ? { drive_id: data.value } : undefined;
  }, [data]);

  const toggleSelectAll = () => {
    props.onSelectAll();
  };

  const toggleFilter = (filter: string) => {
    props.onFilterPress(filter);
  };

  const isSelected = (filter: string) => {
    return props.filters.find((x) => x.label === filter).selected;
  };

  const isNoneSelected = () => {
    return props.filters.every((item) => !item.selected);
  };

  return (
    <Screen>
      <ScreenHeader>
        <ScreenTitle source={info_video_source}>Contacts</ScreenTitle>

        <SearchInput
          preset="white"
          icon="search"
          placeholder="Search names, numbers & more"
          value={props.search_input || ''}
          onChangeText={props.onSubmitSearch}
        />
      </ScreenHeader>

      <Body contentContainerStyle={styles.scroll} onScroll={scrollHandler}>
        <Panel>
          <HorizontalScroll
            horizontal
            contentContainerStyle={styles.button_scroller}
          >
            <FilterButton
              active={props.isAllSelected}
              disabled={isNoneSelected()}
              onPress={toggleSelectAll}
            >
              <FontAwesomeIcon
                icon="check"
                color={props.isAllSelected ? palette.primary_70 : color.text}
              />
              <FilterText weight="bold" active={props.isAllSelected}>
                All
              </FilterText>
            </FilterButton>

            {/* TODO: Show number of contacts for each filter */}
            {props.filters.map((filter, idx) => (
              <FilterButton
                key={idx}
                onPress={() => toggleFilter(filter.label)}
                active={!props.isAllSelected && isSelected(filter.label)}
              >
                <Text>{filter.emoji}</Text>

                <FilterText
                  weight="bold"
                  active={!props.isAllSelected && isSelected(filter.label)}
                >
                  {filter.label}
                </FilterText>
              </FilterButton>
            ))}
          </HorizontalScroll>
        </Panel>

        {/* Favourite contacts */}
        {/* {props.favourites.length > 0 ? (
          <>
            <Heading>
              <Text weight="bold">Favourites</Text>
            </Heading>
            <FavouritesRow>
              {props.favourites.map((contact) => (
                <React.Fragment key={contact.id}>
                  <FavouriteCard
                    contact={contact}
                    onPress={props.onPressViewContact}
                    onPressCall={props.onPressCallContact}
                    onPressMessage={props.onPressMessageContact}
                    onPressVideoCall={props.onPressVideoCallContact}
                    onPressFavourite={props.onPressFavourite}
                  />
                  <Spacer width="16px" />
                </React.Fragment>
              ))}
            </FavouritesRow>
          </>
        ) : null} */}
        {/* Rest of contacts */}
        {/* <Heading>
          <Text weight="bold">{props.isAllSelected ? 'All' : 'Results'}</Text>
        </Heading> */}

        <Card>
          {props.favourites.length ? (
            <>
              <Text variant="h3" weight="semibold">
                <FontAwesomeIcon icon="heart" color="red" size={20} />{' '}
                Favourites
              </Text>

              <View>
                {props.favourites.map((contact) => (
                  <ContactRow
                    key={contact.id}
                    contact={contact}
                    onPressView={props.onPressViewContact}
                    onPressCall={props.onPressCallContact}
                    onPressMessage={props.onPressMessageContact}
                    onPressVideoCall={props.onPressVideoCallContact}
                  />
                ))}
              </View>

              <Text variant="h3" weight="semibold">
                All Contacts
              </Text>
            </>
          ) : null}

          {props.contacts.length === 0 ? (
            <Placeholder onPress={props.onPressAdd}>
              <Text>
                You have no contacts. Press here to make a new contact.
              </Text>
            </Placeholder>
          ) : (
            <View>
              {props.contacts.map((contact) => (
                <ContactRow
                  key={contact.id}
                  contact={contact}
                  onPressView={props.onPressViewContact}
                  onPressCall={props.onPressCallContact}
                  onPressMessage={props.onPressMessageContact}
                  onPressVideoCall={props.onPressVideoCallContact}
                />
              ))}
            </View>
          )}
        </Card>
      </Body>

      <FloatingButtons style={floating_action_styles}>
        <BottomActionButton
          icon="comment-lines"
          size="large"
          onPress={openSMSApp}
        >
          Message
        </BottomActionButton>

        <BottomActionButton icon="phone" size="large" onPress={openPhone}>
          Call
        </BottomActionButton>

        <BottomActionButton icon="plus" size="large" onPress={props.onPressAdd}>
          New contact
        </BottomActionButton>
      </FloatingButtons>
    </Screen>
  );
};

const Screen = styled.View`
  flex: 1;
`;

const Body = styled(Animated.ScrollView)`
  flex: 1;
`;

const styles = StyleSheet.create({
  scroll: {
    paddingHorizontal: 20,
    paddingBottom: 100,
    gap: 20,
  },
  button_scroller: {
    gap: 10,
    padding: 20,
  },
});

const SearchInput = styled(TextInput)`
  width: 500px;
`;

const HorizontalScroll = styled.ScrollView`
  flex-grow: 1;
`;

const FilterButton = styled.TouchableOpacity<{
  active?: boolean;
}>`
  flex-direction: row;
  align-items: center;
  gap: 10px;
  border-radius: 20px;
  background-color: ${(props) =>
    props.active ? palette.Pale_blue : palette.lightGrey1};
  padding: 10px 15px;
  height: 50px;
`;

const FilterText = styled(Text)<{ active?: boolean }>`
  color: ${(props) => (props.active ? palette.primary_70 : color.text)};
`;

const Card = styled(Panel)`
  padding: 20px;
  gap: 20px;
`;

const Placeholder = styled.TouchableOpacity`
  padding-top: ${spacing[7]};
  padding-bottom: ${spacing[7]};
`;

const FloatingButtons = styled(FloatingActions)`
  gap: 20px;
`;

const BottomActionButton = styled(Button)`
  width: 180px;
`;

export default ContactsScreenView;
